import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import CommunicationsDestinationPicker from "./CommunicationsDestinationPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function toLocalInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export default function CommunicationsSessionActions({
  session,
  destinations,
  providers = [],
  maxDestinations = 3,
  onChanged,
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState(session.title || "");
  const [description, setDescription] = useState(session.description || "");
  const [scheduledAt, setScheduledAt] = useState(toLocalInput(session.scheduled_at));
  const [visibility, setVisibility] = useState(session.visibility || "internal");
  const [orientation, setOrientation] = useState(session.orientation || "landscape");
  const [selectedDestinations, setSelectedDestinations] = useState(session.selected_destination_ids || []);

  useEffect(() => {
    setTitle(session.title || "");
    setDescription(session.description || "");
    setScheduledAt(toLocalInput(session.scheduled_at));
    setVisibility(session.visibility || "internal");
    setOrientation(session.orientation || "landscape");
    setSelectedDestinations(session.selected_destination_ids || []);
  }, [session]);

  const save = async () => {
    if (!title.trim()) return toast.error("Session title is required");
    setBusy(true);
    try {
      await axios.patch(`${API}/communications/rooms/${session.id}`, {
        title: title.trim(),
        description: description.trim(),
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : "",
        visibility,
        orientation,
        selected_destination_ids: session.session_type === "live" ? selectedDestinations : undefined,
      });
      toast.success("Session updated");
      setEditing(false);
      await onChanged?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update session");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const confirmed = window.confirm(
      `Delete “${session.title || "this session"}”?\n\nAny saved recording will remain in the Recordings library.`
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/communications/rooms/${session.id}`);
      toast.success("Session deleted. Saved recordings were preserved.");
      await onChanged?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not delete session");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={busy}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setEditing(true);
          }}
          className="p-2 rounded-lg text-slate-400 hover:text-[#9dc1ff] hover:bg-white/5"
          title="Edit session"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            remove();
          }}
          className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10"
          title="Delete session"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[130] bg-black/80 flex items-center justify-center p-4" onClick={(event) => event.stopPropagation()}>
          <div className="w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-2xl border border-slate-700 bg-[#111318] text-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-800">
              <div>
                <div className="text-xs uppercase tracking-[.2em] text-kingdom-gold font-bold">Session management</div>
                <h2 className="text-2xl font-semibold mt-1">Edit session</h2>
              </div>
              <button type="button" onClick={() => setEditing(false)} className="p-2 rounded-lg hover:bg-white/5"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto">
              {session.session_type === "live" && (
                <CommunicationsDestinationPicker destinations={destinations} providers={providers} selectedIds={selectedDestinations} maxDestinations={maxDestinations} onChange={setSelectedDestinations} />
              )}
              <div><label className="text-sm text-slate-300">Title</label><Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2 bg-[#0b0d11] border-slate-700 text-white" /></div>
              <div><label className="text-sm text-slate-300">Description</label><textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} className="mt-2 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-3 text-white" /></div>
              <div><label className="text-sm text-slate-300">Schedule</label><Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="mt-2 bg-[#0b0d11] border-slate-700 text-white" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-sm text-slate-300">Visibility</label><select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="mt-2 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-2.5"><option value="public">Public</option><option value="unlisted">Unlisted</option><option value="internal">Internal</option></select></div>
                <div><label className="text-sm text-slate-300">Orientation</label><select value={orientation} onChange={(e) => setOrientation(e.target.value)} className="mt-2 w-full rounded-md bg-[#0b0d11] border border-slate-700 p-2.5"><option value="landscape">Landscape 16:9</option><option value="portrait">Portrait 9:16</option><option value="square">Square 1:1</option></select></div>
              </div>
              <div className="rounded-xl border border-slate-700 bg-[#0b0d11] p-4 text-sm text-slate-400">
                Program layouts are chosen visually inside Studio, directly underneath the presenter/program preview.
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-800 flex justify-end gap-3">
              <Button type="button" variant="outline" className="border-slate-600 text-white" onClick={() => setEditing(false)}>Cancel</Button>
              <Button type="button" disabled={busy} onClick={save} className="bg-[#8bb6ff] text-slate-950">{busy ? "Saving…" : "Save changes"}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
